import {Container, Graphics, Point, Sprite} from 'pixi.js';
import {
    consoleLogger,
    CoreDI,
    IAnimation,
    inject,
    ObjectBuilder,
    ScreenParameters,
    Tween
} from '@gongxigames/framework/core';
import {ReelsView, ReelSymbol} from '@gongxigames/framework/slot/modules/reels';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {GameConfig, IReelsScaleService} from '@gongxigames/framework/slot/types';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {ReelsCascadingAddData, ReelsCascadingMoveData} from '@gongxigames/framework/slot/modules/reelsCascading';
import {ReelConstants, SymbolPosition} from '@gongxigames/framework/slot/utils';
import {ReelsCascadingConfig} from '@gongxigames/framework/slot/types/ReelsCascadingConfig';
import {View} from '@gongxigames/framework/slot/fragments/module/view';

export class ReelsCascadingView extends View {
    public static readonly NAME: symbol = ReelsView.NAME;
    public static layer = LAYERS.REELS;
    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;
    @inject(DI.ReelsScaleService)
    protected _reelsScaleService!: IReelsScaleService;
    protected symbols: Map<string, ReelSymbol> = new Map();
    protected reelSetContainer: Container = new Container();
    protected reelSetAnimationContainer: Container = new Container();
    protected reelsLayerToBeMasked: Container = new Container();
    protected animationToBeMaskedContainer: Container = new Container();
    protected currentMask?: Graphics;
    protected _moveSymbolsTween: Tween[] = [];
    protected _isTurboSpin = false;

    public addSymbol(addSymbolData: ReelsCascadingAddData): void {
        const {symbolID, coordinates} = addSymbolData;
        const symbol = new ReelSymbol();

        symbol.init(this.getSymbolsSprite());
        symbol.setSymbol(symbolID);
        symbol.position = this.getSymbolPositionByCoordinates(coordinates);
        this.reelSetContainer.addChild(symbol);
        this.addSymbolToMap(coordinates, symbol);
    }

    public async moveSymbol(moveData: ReelsCascadingMoveData): Promise<ReelSymbol | undefined> {
        return new Promise(resolve => {
            const {fromCoordinates, toCoordinates, delay} = moveData;
            const symbol = this.getSymbol(fromCoordinates);
            const toPosition = this.getSymbolPositionByCoordinates(toCoordinates);
            const movingDuration = this.getSymbolFallingDuration(Math.abs(toCoordinates.y - fromCoordinates.y));

            if (symbol) {
                this.removeSymbolFromMap(fromCoordinates);

                const tween = new Tween(symbol)
                    .to({x: toPosition.x, y: toPosition.y}, movingDuration)
                    .delay(delay)
                    .easing(this.reelsCascadingConfig.movingEasing)
                    .onComplete(() => {
                        this.addSymbolToMap(toCoordinates, symbol);
                        resolve(symbol);
                        this._moveSymbolsTween.shift();
                    })
                    .onStop(() => {
                        const quickStopDuration = Math.abs(toPosition.y - symbol.position.y) / (ReelConstants.ROW_AMOUNT * 2);

                        this.updateManager.addTween(symbol)
                            .to(
                                {x: toPosition.x, y: toPosition.y}, quickStopDuration)
                            .onComplete(() => {
                                this.addSymbolToMap(toCoordinates, symbol);
                                resolve(symbol);
                            })
                            .start();
                    })
                    .start();

                this._moveSymbolsTween.push(tween);
                this.updateManager.add(tween);
            } else {
                consoleLogger.warn('Symbol doesn\'t exist in coordinates: ', fromCoordinates);
                resolve(symbol);
            }
        });
    }

    public updateSymbol(symbolId: number, coordinates: SymbolPosition): void {
        const symbol = this.getSymbol(coordinates);

        if (symbol) {
            symbol.setSymbol(symbolId);
        }
    }

    public removeSymbolByPosition(coordinates: SymbolPosition): void {
        const symbol = this.getSymbol(coordinates);

        if (symbol) {
            this.removeSymbolFromMap(coordinates);
            symbol.destroy();
        }
    }

    public removeAllSymbols(): void {
        for (const [key, value] of this.symbols.entries()) {
            this.symbols.delete(key);
            value.destroy();
        }
    }

    public setSymbolVisible(coordinates: SymbolPosition, visible: boolean): void {
        const symbol = this.getSymbol(coordinates);

        if (symbol) {
            symbol.visible = visible;
        }
    }

    public setAllSymbolsVisible(visible: boolean): void {
        this.symbols.forEach(symbol => {
            symbol.visible = visible;
        });
    }

    public playLandingBounceTween(coordinates: SymbolPosition): Promise<void> {
        return new Promise(resolve => {
            const symbol = this.getSymbol(coordinates);
            const {landingBounceOffset, landingBounceDuration} = this.reelsCascadingConfig;

            if (symbol) {
                this.updateManager.addTween(symbol)
                    .to({y: symbol.y - landingBounceOffset}, landingBounceDuration)
                    .easing(this.reelsCascadingConfig.landingEasing)
                    .onComplete(() => {
                        this.updateManager.addTween(symbol)
                            .to({y: symbol.y + landingBounceOffset}, landingBounceDuration)
                            .easing(this.reelsCascadingConfig.landingEasing)
                            .onComplete(() => {
                                resolve();
                            })
                            .start();
                    })
                    .start();
            } else {
                consoleLogger.warn('Symbol doesn\'t exist in coordinates: ', coordinates);
                resolve(symbol);
            }
        });
    }

    public addHitAnimation(coordinates: SymbolPosition): Promise<ReelSymbol | undefined> {
        return new Promise(resolve => {
            const symbol = this.getSymbol(coordinates);

            if (symbol) {
                const symbolType = symbol.symbolType;
                const animation = this.createSymbolHitAnimation(symbolType);

                if (!animation) {
                    consoleLogger.warn(`SymbolType ${symbolType} hit animation is null!`);
                    resolve(symbol);

                    return;
                }

                symbol.visible = false;
                symbol.animationContainer = new Container();
                symbol.animationContainer.addChild(animation.container);
                symbol.updateAnimationContainerPosition();
                this.reelSetAnimationContainer.addChild(symbol.animationContainer);

                animation.container.x = ReelConstants.SYMBOL.width / 2;
                animation.container.y = ReelConstants.SYMBOL.height / 2;
                animation.start();
                animation.setOnComplete(() => {
                    symbol.visible = true;
                    resolve(symbol);
                });
                this.updateManager.add(animation);
            } else {
                consoleLogger.warn('Symbol doesn\'t exist in coordinates: ', coordinates);
                resolve(symbol);
            }
        });
    }

    public killEverythingFromAnimationsLayer(): void {
        this.reelSetAnimationContainer.children.length = 0;
    }

    protected getSymbolPositionByCoordinates(coordinates: SymbolPosition): Point {
        const reelPositions = ReelConstants.REELS[coordinates.x];
        let posY = 0;

        if (reelPositions.symsPositions[coordinates.y]?.y !== undefined) {
            posY = reelPositions.symsPositions[coordinates.y].y;
        } else {
            posY = reelPositions.y + ReelConstants.SYMBOL_SLOT.height * coordinates.y;
        }

        return new Point(reelPositions.x, posY);
    }

    protected getSymbolsSprite(): Sprite {
        return this._objectBuilder.buildSprite(ObjectBuilder.resources.game_sprite.symbols);
    }

    public setupReelsLayers(): void {
        this.container.removeChildren();

        this.reelSetContainer = new Container();
        this.reelSetAnimationContainer = new Container();
        this.reelsLayerToBeMasked = new Container();
        this.animationToBeMaskedContainer = new Container();

        this.container.addChild(this.reelsLayerToBeMasked);

        this.reelsLayerToBeMasked.addChild(this.reelSetContainer);
        this.reelsLayerToBeMasked.addChild(this.animationToBeMaskedContainer);
        this.container.addChild(this.reelSetAnimationContainer);
        this.setupReelSetMask();
    }

    public onOrientationChanged(): void {
        this.scaleView();
    }

    public onResized(screenParams: ScreenParameters): void {
        this.scaleView();
    }

    public fadeAway(shouldShow = false): void {
        this._updateManager.addTween(this.container)
            .to({alpha: shouldShow ? 1.0 : 0.0},
                shouldShow ? this.reelsCascadingConfig.fadeInDuration : this.reelsCascadingConfig.fadeOutDuration)
            .start();
    }

    public onForceStopReels(): void {
        this._moveSymbolsTween.forEach(tween => {
            if (tween && tween.isPlaying) {
                tween.stop();

                this.updateManager.remove(tween);
            }
        });

        this._moveSymbolsTween = [];
    }

    protected scaleView(): void {
        if (this.isPlatformDesktop()) {
            this._reelsScaleService.reelsDesktopScale(this.container);
        } else {
            this._reelsScaleService.scaleByCurrentScreenOrientation(this.container);
        }
    }

    /**
     * this function draws reel mask according to reel.tsx scene
     * for the first and last reels we draw rect with double width
     */
    protected setupReelSetMask(): void {
        if (this.currentMask && this.reelsLayerToBeMasked.children.indexOf(this.currentMask) >= 0) {
            this.reelsLayerToBeMasked.removeChild(this.currentMask);
        }

        this.currentMask = new Graphics();
        this.currentMask.beginFill(0xffffff);

        for (let i = 0; i < ReelConstants.REELS.length; i++) {
            this.drawRectForSingleReel(i);
        }
        this.currentMask.endFill();

        this.reelsLayerToBeMasked.addChild(this.currentMask);
        this.reelsLayerToBeMasked.mask = this.currentMask;
    }

    protected drawRectForSingleReel(reelIndex: number): void {
        const symbol = ReelConstants.SYMBOL_SLOT;
        const reelsPositions = ReelConstants.REELS;
        const simsPositions = reelsPositions[reelIndex].symsPositions;
        const x = reelIndex === 0 ? reelsPositions[reelIndex].x - symbol.width : reelsPositions[reelIndex].x;
        const y = reelsPositions[reelIndex].y;
        let width = 0;

        if (reelIndex === 0) {
            width = reelsPositions[reelIndex + 1].x - reelsPositions[reelIndex].x + symbol.width;
        } else if (reelIndex === reelsPositions.length - 1) {
            width = symbol.width * 2;
        } else {
            width = reelsPositions[reelIndex + 1].x - reelsPositions[reelIndex].x;
        }
        const height = simsPositions[simsPositions.length - 1].y + symbol.height - y;

        this.currentMask?.drawRect(x, y, width, height);
    }

    protected createSymbolHitAnimation(symbolIndex: number): IAnimation | undefined {
        const animationData = this.reelsCascadingConfig.hitAnimationsMap[symbolIndex];

        if (animationData) {
            return this._spineUtils.createSpineAnimation(animationData.spineName, animationData.animationName);
        }
    }

    protected getSymbol(pos: SymbolPosition): ReelSymbol | undefined {
        return this.symbols.get(JSON.stringify(pos));
    }

    protected addSymbolToMap(position: SymbolPosition, symbol: ReelSymbol): void {
        this.symbols.set(JSON.stringify(position), symbol);
    }

    protected removeSymbolFromMap(position: SymbolPosition): void {
        this.symbols.delete(JSON.stringify(position));
    }

    protected getSymbolFallingDuration(distance: number): number {
        return distance * (this._isTurboSpin ? this.reelsCascadingConfig.fallingSpeedTurbo : this.reelsCascadingConfig.fallingSpeed);
    }

    protected get reelsCascadingConfig(): ReelsCascadingConfig {
        if (this._gameConfig.reelsCascadingConfig === undefined) {
            throw new Error('ReelsCascadingConfig is not defined. Add it in your GameConfig');
        }

        return this._gameConfig.reelsCascadingConfig!;
    }

    public set isTurboSpin(value: boolean) {
        this._isTurboSpin = value;
    }
}